const DEFAULT_BASE_URL = 'https://app.biel.ai/api/v1';

export class BielService {
  private project: string;
  private apiKey: string;
  private errorMessage404: string;
  private errorMessage403: string;
  private errorMessageDefault: string;
  private baseUrl: string;

  constructor(
    project: string,
    errorMessage404: string,
    errorMessage403: string,
    errorMessageDefault: string,
    apiKey: string = '',
    baseUrl: string = ''
  ) {
    this.project = project;
    this.apiKey = apiKey;
    this.errorMessage404 = errorMessage404;
    this.errorMessage403 = errorMessage403;
    this.errorMessageDefault = errorMessageDefault;
    this.baseUrl = baseUrl || DEFAULT_BASE_URL;
  }

  private getHeaders(includeContentType: boolean = true): HeadersInit {
    const headers: HeadersInit = {};

    if (includeContentType) {
      headers['Content-Type'] = 'application/json; charset=UTF-8';
    }

    if (this.apiKey) {
      headers['Authorization'] = `Api-Key ${this.apiKey}`;
    }

    return headers;
  }

  async getProjectDetails(): Promise<
    | {
        whitelabel: boolean;
        suggestedQuestions: string[];
        initialMessage: string;
        filters: { title: string }[];
        think_mode: boolean;
        mcp_enabled: boolean;
        mcp_server_name: string;
        mcp_metadata_tag: string;
        actions: { name: string; button_text: string; url: string }[];
      }
    | string
  > {
    try {
      const response = await fetch(`${this.baseUrl}/projects/${this.project}/`, {
        headers: this.getHeaders(false),
      });

      if (response.status === 404) {
        return this.errorMessage404;
      }

      if (response.status === 403) {
        return this.errorMessage403;
      }

      if (!response.ok) {
        return this.errorMessageDefault;
      }

      const data = await response.json();
      return {
        whitelabel: data.whitelabel,
        suggestedQuestions: data.suggested_questions,
        initialMessage: data.initial_message,
        filters: data.filters || [],
        think_mode: data.think_mode || false,
        mcp_enabled: data.mcp_enabled || false,
        mcp_server_name: data.mcp_server_name || '',
        mcp_metadata_tag: data.mcp_metadata_tag || '',
        actions: data.actions || [],
      };
    } catch (error) {
      console.error('Error fetching project details:', error);
      return this.errorMessageDefault;
    }
  }

  async sendFeedback(messageId: string, feedback: { reaction: 0 | 1 }) {
    try {
      const response = await fetch(`${this.baseUrl}/messages/${messageId}/feedback/`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(feedback),
      });

      if (!response.ok) {
        console.error('Error:', response.status);
      }
    } catch (error) {
      console.error('Error sending feedback:', error);
    }
  }

  async sendQuery(
    q: string,
    selected_filter_title: string
  ): Promise<{ title: string; fragment: string; url: string }[]> {
    try {
      const searchUrl = new URL(`${this.baseUrl}/projects/${this.project}/search/`);
      searchUrl.searchParams.set('q', q);
      searchUrl.searchParams.set('url', window.location.href);
      if (selected_filter_title) {
        searchUrl.searchParams.set('filter_title', selected_filter_title);
      }

      const response = await fetch(searchUrl.toString(), { headers: this.getHeaders(false) });

      if (!response.ok) {
        console.error('Error:', response.status);
        return [];
      }

      const textData = await response.text();
      if (!textData) {
        return [];
      }

      const data = JSON.parse(textData);

      return data;
    } catch (error) {
      console.error('Error sending query:', error);
      return [];
    }
  }

  async searchClicked() {
    try {
      const response = await fetch(`${this.baseUrl}/projects/${this.project}/search/click/`, {
        method: 'POST',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        console.error('Error:', response.status);
      }
    } catch (error) {
      console.error('Error sending search clicked:', error);
    }
  }

  private parseMarkdownSources(sourcesString: string): { title: string; url: string }[] {
    if (!sourcesString || typeof sourcesString !== 'string') {
      return [];
    }

    const lines = sourcesString.split('\n').filter((line) => line.trim());
    const sources: { title: string; url: string }[] = [];

    lines.forEach((line) => {
      const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
      let match;

      while ((match = markdownLinkRegex.exec(line)) !== null) {
        const title = match[1].trim();
        const url = match[2].trim();

        if (title && url) {
          sources.push({ title, url });
        }
      }
    });

    return sources;
  }

  async getConversationMessages(chatId: string): Promise<
    {
      text: string;
      sender: 'user' | 'ai';
      messageId?: string;
      feedback?: 0 | 1;
      sources?: { title: string; url: string }[];
      isPartial: boolean;
    }[]
  > {
    const url = `${this.baseUrl}/chats/${chatId}`;

    try {
      const response = await fetch(url, {
        headers: this.getHeaders(false),
      });

      if (response.status === 403) {
        throw new Error('FORBIDDEN_403');
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error fetching conversation messages:', response.status, errorText);
        return [];
      }

      const data = await response.json();

      const transformedMessages =
        data.messages?.map((msg: any) => ({
          text: msg.content,
          sender: msg.is_human_message ? 'user' : 'ai',
          messageId: msg.uuid,
          feedback: msg.feedback,
          sources: msg.sources ? this.parseMarkdownSources(msg.sources) : [],
          isPartial: false,
        })) || [];

      return transformedMessages;
    } catch (error) {
      console.error('Error fetching conversation messages:', error);
      if (error.message === 'FORBIDDEN_403') {
        throw error;
      }
      return [];
    }
  }
}
